import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import OptionalComponent from '@ui/optional-component/optional-component';
import Quill from '@ui/quill/quill';
import RenderQuillContent from '@ui/quill/render-quill-content';
import Text from '@ui/text/text/text';
import toastTrigger from '@ui/toast/toast-trigger';

import { useAppSelector } from '@/store/hooks';
import {
  getPublishedStatus,
  selectProjectName,
  selectWorkingProjectDescription,
} from '@/store/slices/project/selectors';
import { selectUserChannel } from '@/store/slices/user/selectors';

import { calculateDuration } from '@/utils/date/calculate-duration';
import { formatDate } from '@/utils/date/format-date';
import getPrefixUrl from '@/utils/get-prefix-url';

import { Session } from '@/types/session.types';
import { ICalendarDetailMetadata } from '../../settings.types';

import { generateIcsFileContent } from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/emails/helpers/calendar-invites';

interface ICalendarDetailProps {
  metadata: ICalendarDetailMetadata;
  setMetadata: (data: ICalendarDetailMetadata) => void;
  session: Session;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
}

const CalendarDetail = ({ session, setIsEditing, isEditing, setMetadata, metadata }: ICalendarDetailProps) => {
  const projectDescription = useAppSelector(selectWorkingProjectDescription);
  const projectName = useAppSelector(selectProjectName);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const channel = useAppSelector(selectUserChannel);

  const params = useParams<{ projectUuid: string; language: string }>();
  const language = params.language || 'en';

  const {
    url,
    calendarTitle,
    calendarDescription,
    formattedStartTime,
    formattedEndTime,
    startTimestamp,
    endTimestamp,
    displayUrl,
  } = useMemo(() => {
    const startTimestamp = Number(session.timestamp) || Date.now();
    const endTimestamp = Number(session.end_timestamp) || Date.now();

    const startDateObject = new Date(startTimestamp);
    const endDateObject = new Date(endTimestamp);

    const baseDateFormat = {
      weekday: undefined,
      year: undefined,
      month: undefined,
      day: undefined,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    const fullDateFormat = {
      ...baseDateFormat,
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    const startTime = formatDate(startTimestamp, fullDateFormat);

    const endTimeOptions = startDateObject.getDate() === endDateObject.getDate() ? baseDateFormat : fullDateFormat;

    const endTime = formatDate(endDateObject, endTimeOptions);

    const baseURL = getPrefixUrl(channel?.url);
    const shortLink = import.meta.env.VITE_URL_SHORTENER;
    const resolvedUrl = `${shortLink}/${publishedStatus?.url}`;
    const displayUrl = `${baseURL}/${publishedStatus?.url}`;

    const resolvedTitle = metadata.name ?? session.calendar_settings?.content?.[language]?.title ?? projectName;
    const resolvedDescription =
      metadata.description ?? session.calendar_settings?.content?.[language]?.description ?? projectDescription;

    return {
      url: resolvedUrl,
      calendarTitle: resolvedTitle,
      calendarDescription: resolvedDescription,
      formattedStartTime: startTime,
      formattedEndTime: endTime,
      startTimestamp,
      endTimestamp,
      displayUrl,
    };
  }, [channel, publishedStatus, metadata, language, session, projectName, projectDescription]);

  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMetadata({ ...metadata, name: e.target.value });
    },
    [metadata, setMetadata]
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setMetadata({ ...metadata, description: value });
    },
    [metadata, setMetadata]
  );

  const toggleEditing = useCallback(() => setIsEditing((prev) => !prev), [setIsEditing]);

  const handleDownload = useCallback(async () => {
    if (!params.projectUuid) return;

    try {
      const emailTranslations = await import(`../../../../../../../i18n/${params.language}/email.json`);
      const icsFileContent = generateIcsFileContent(
        startTimestamp,
        endTimestamp,
        calendarTitle,
        calendarDescription,
        url,
        params.projectUuid,
        emailTranslations,
        false
      );

      if (icsFileContent) {
        const { icsFileText } = icsFileContent;
        const filename = (session.title.base.replace(/[^0-9a-zA-Z]/g, '-') || 'calendar-invite') + '.ics';
        const blob = new Blob([icsFileText], { type: 'text/calendar' });
        const downloadUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;

        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      } else {
        toastTrigger(<div className="S1">Failed to generate ICS file content.</div>, {
          type: 'error',
          autoClose: 10000,
        });
      }
    } catch (error) {
      console.error('Download failed:', error);
      toastTrigger(<div className="S1">Failed to download ICS file.</div>, {
        type: 'error',
        autoClose: 10000,
      });
    }
  }, [
    calendarDescription,
    calendarTitle,
    endTimestamp,
    params.language,
    params.projectUuid,
    session.title.base,
    startTimestamp,
    url,
  ]);

  return (
    <Container style={{ transition: 'height 200ms' }}>
      <Container
        className={'flex padding-20 gap-20 height-100-percent'}
        borderRadius="12px"
        hoverBackground={!isEditing ? 'var(--gray-800)' : ''}
        background={!isEditing ? 'var(--gray-900)' : 'var(--gray-800)'}
      >
        <OptionalComponent display={isEditing}>
          <div className="flex-column gap-10 flex-1">
            <div className="flex ai-center jc-space-between">
              <InputWithOverlay
                wrapperClass="flex-1"
                isBorderless
                typography="H4"
                designSize="small"
                design={null}
                inputProps={{
                  tabIndex: 0,
                  value: calendarTitle,
                  className: `padding-2 radius-4`,
                  disabled: !isEditing,
                  onChange: handleNameChange,
                }}
              />
            </div>
            <div className="flex">
              <div className="flex-column ai-flex-start gap-4" style={{ flex: '.75' }}>
                <Text className="L3" color="var(--gray-500)">
                  DATE
                </Text>
                <Text color="var(--gray-200)" className="C1">
                  {formattedStartTime} - {endTimestamp ? formattedEndTime : 'N/A'}
                </Text>
              </div>
              <div className="flex-column ai-flex-start gap-4">
                <Text className="L3" color="var(--gray-500)">
                  DURATION
                </Text>
                <Text className="S3">
                  {calculateDuration({ startDate: startTimestamp, endDate: endTimestamp, selectedTimezone: userTz })}
                </Text>
              </div>
            </div>
            <OptionalComponent display={!!publishedStatus?.url}>
              <div className="flex-column ai-flex-start gap-4">
                <Text className="L3" color="var(--gray-500)">
                  LOCATION
                </Text>
                <Text className="S3">{displayUrl}</Text>
              </div>
            </OptionalComponent>
            <div className="flex-column ai-flex-start gap-4">
              <Text className="L3" color="var(--gray-500)">
                DESCRIPTION
              </Text>
              <Quill
                html={calendarDescription || '<div>{{Project description}}</div>'}
                onChange={handleDescriptionChange}
              />
            </div>
          </div>
        </OptionalComponent>
        <OptionalComponent display={!isEditing}>
          <div className="flex-column gap-10 flex-1">
            <div className="flex ai-center jc-space-between">
              <Text className="H4">{calendarTitle}</Text>
              <Button className="padding-0" onClick={toggleEditing}>
                <Container
                  className={'flex fd-row jc-center ai-center radius-full padding-8'}
                  background="var(--gray-800)"
                  hoverBackground="var(--gray-700)"
                >
                  <Icon name={ICONS.EDIT} size={16} color="var(--gray-200)" />
                </Container>
              </Button>
            </div>
            <div className="flex-column ai-flex-start gap-4">
              <Text className="S3">
                <RenderQuillContent description={calendarDescription || '{{Project description}}'} />
              </Text>
            </div>
            <div className="flex ai-center gap-8">
              <Container
                background="var(--gray-700)"
                width="fit-content"
                className="flex ai-center gap-10 padding-h-12 padding-v-8"
                borderRadius="100px"
              >
                <Icon name={ICONS.CALENDAR_SETTINGS} />
                <Text color="var(--gray-200)" className="C1">
                  {formattedStartTime} - {endTimestamp ? formattedEndTime : 'N/A'}
                </Text>
              </Container>
              <Button
                width="32px"
                height="32px"
                borderRadius="100px"
                backgroundColor="var(--gray-800)"
                hoverBackgroundColor="var(--gray-700)"
                className="radius-full padding-v-6 padding-h-0 flex fd-row jc-center ai-center padding-0"
                onClick={handleDownload}
              >
                <Icon name={ICONS.DOWNLOAD} size={16} color="var(--gray-200)" />
              </Button>
            </div>
          </div>
        </OptionalComponent>
      </Container>
    </Container>
  );
};

export default CalendarDetail;
